import { useState, useEffect, useCallback } from "react";
import { useAuth, API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import {
  Truck,
  Plus,
  MagnifyingGlass,
  PencilSimple,
  Trash,
  Phone,
  EnvelopeSimple,
  MapPin,
  Buildings,
  X
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { usePermissions } from "@/hooks/usePermissions";

const emptyForm = {
  nom: "", prenom: "", type_fournisseur: "professionnel", entreprise: "",
  tva_numero: "", telephone: "", email: "", adresse: "", code_postal: "",
  ville: "", pays: "Belgique", iban: "", notes: ""
};

const Fournisseurs = () => {
  const { can } = usePermissions();
  const [fournisseurs, setFournisseurs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const canCreate = can("fournisseurs", "create");
  const canEdit = can("fournisseurs", "update");
  const canDelete = can("fournisseurs", "delete");

  const fetchData = useCallback(async () => {
    try {
      const params = new URLSearchParams({ page, per_page: 20 });
      if (search) params.set("search", search);
      const res = await axios.get(`${API}/fournisseurs?${params}`);
      setFournisseurs(res.data.data || []);
      setTotalPages(res.data.pages || 1);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, [page, search]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const openCreate = () => { setEditId(null); setForm(emptyForm); setDialogOpen(true); };
  const openEdit = (f) => { setEditId(f.fournisseur_id); setForm({ ...emptyForm, ...f }); setDialogOpen(true); };

  const handleSave = async () => {
    if (!form.nom) { toast.error("Nom requis"); return; }
    setSaving(true);
    try {
      if (editId) {
        await axios.put(`${API}/fournisseurs/${editId}`, form);
        toast.success("Fournisseur mis a jour");
      } else {
        await axios.post(`${API}/fournisseurs`, form);
        toast.success("Fournisseur cree");
      }
      setDialogOpen(false);
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/fournisseurs/${id}`);
      toast.success("Fournisseur supprime");
      fetchData();
    } catch { toast.error("Erreur suppression"); }
  };

  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  return (
    <div className="space-y-6" data-testid="fournisseurs-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-heading-2 text-slate-900 dark:text-slate-100">Fournisseurs</h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">Gestion des fournisseurs et vendeurs</p>
        </div>
        {canCreate && (
          <Button onClick={openCreate} className="bg-orange-600 hover:bg-orange-700 text-white" data-testid="add-fournisseur-btn">
            <Plus size={16} className="mr-1.5" /> Nouveau fournisseur
          </Button>
        )}
      </div>

      <div className="card p-4">
        <div className="relative">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} placeholder="Rechercher par nom, entreprise, email..." className="pl-9" data-testid="fournisseur-search" />
        </div>
      </div>

      {loading ? (
        <div className="card p-12 text-center"><div className="spinner h-8 w-8 mx-auto mb-3"></div></div>
      ) : fournisseurs.length === 0 ? (
        <div className="card p-12 text-center">
          <Truck size={48} className="mx-auto text-slate-300 dark:text-slate-600 mb-3" />
          <p className="text-sm text-slate-500">Aucun fournisseur</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {fournisseurs.map(f => (
            <div key={f.fournisseur_id} className="card p-5 hover:shadow-md transition-all" data-testid={`fournisseur-card-${f.fournisseur_id}`}>
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${f.type_fournisseur === "professionnel" ? "bg-orange-100 dark:bg-orange-900/30" : "bg-blue-100 dark:bg-blue-900/30"}`}>
                    {f.type_fournisseur === "professionnel" ? <Buildings size={20} className="text-orange-600" /> : <Truck size={20} className="text-blue-600" />}
                  </div>
                  <div>
                    <h3 className="font-semibold text-sm text-slate-900 dark:text-slate-100">{f.entreprise || `${f.prenom || ""} ${f.nom}`}</h3>
                    <span className={`text-[10px] px-2 py-0.5 rounded-full ${f.type_fournisseur === "professionnel" ? "bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300" : "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300"}`}>
                      {f.type_fournisseur === "professionnel" ? "Pro" : "Particulier"}
                    </span>
                  </div>
                </div>
                <div className="flex gap-1">
                  {canEdit && <button onClick={() => openEdit(f)} className="p-1.5 rounded hover:bg-slate-100 dark:hover:bg-slate-800" data-testid={`edit-fournisseur-${f.fournisseur_id}`}><PencilSimple size={14} className="text-slate-500" /></button>}
                  {canDelete && (
                    <AlertDialog>
                      <AlertDialogTrigger asChild><button className="p-1.5 rounded hover:bg-red-50 dark:hover:bg-red-900/20" data-testid={`delete-fournisseur-${f.fournisseur_id}`}><Trash size={14} className="text-red-400" /></button></AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader><AlertDialogTitle>Supprimer ce fournisseur ?</AlertDialogTitle><AlertDialogDescription>Cette action est irreversible.</AlertDialogDescription></AlertDialogHeader>
                        <AlertDialogFooter><AlertDialogCancel>Annuler</AlertDialogCancel><AlertDialogAction onClick={() => handleDelete(f.fournisseur_id)} className="bg-red-600 hover:bg-red-700">Supprimer</AlertDialogAction></AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  )}
                </div>
              </div>
              <div className="space-y-1.5 text-xs text-slate-500 dark:text-slate-400">
                {f.tva_numero && <div className="flex items-center gap-1.5">TVA: {f.tva_numero}</div>}
                {f.telephone && <div className="flex items-center gap-1.5"><Phone size={12} /> {f.telephone}</div>}
                {f.email && <div className="flex items-center gap-1.5"><EnvelopeSimple size={12} /> {f.email}</div>}
                {f.adresse && <div className="flex items-center gap-1.5"><MapPin size={12} /> {f.adresse}</div>}
                {f.iban && <div className="truncate">IBAN: {f.iban}</div>}
              </div>
            </div>
          ))}
        </div>
      )}

      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2 pt-2">
          <Button size="sm" variant="outline" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1}>Precedent</Button>
          <span className="text-sm text-slate-500">{page} / {totalPages}</span>
          <Button size="sm" variant="outline" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page >= totalPages}>Suivant</Button>
        </div>
      )}

      {/* Dialog */}
      <Dialog open={dialogOpen} onOpenChange={(v) => !v && setDialogOpen(false)}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editId ? "Modifier" : "Nouveau"} fournisseur</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="form-label">Nom *</Label><Input value={form.nom} onChange={e => set("nom", e.target.value)} data-testid="fournisseur-nom" /></div>
              <div><Label className="form-label">Prenom</Label><Input value={form.prenom} onChange={e => set("prenom", e.target.value)} data-testid="fournisseur-prenom" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="form-label">Type</Label>
                <Select value={form.type_fournisseur} onValueChange={v => set("type_fournisseur", v)}>
                  <SelectTrigger data-testid="fournisseur-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="professionnel">Professionnel</SelectItem>
                    <SelectItem value="particulier">Particulier</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label className="form-label">Entreprise</Label><Input value={form.entreprise} onChange={e => set("entreprise", e.target.value)} data-testid="fournisseur-entreprise" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="form-label">N TVA</Label><Input value={form.tva_numero} onChange={e => set("tva_numero", e.target.value)} placeholder="BE0123456789" data-testid="fournisseur-tva" /></div>
              <div><Label className="form-label">IBAN</Label><Input value={form.iban} onChange={e => set("iban", e.target.value)} placeholder="BE68..." data-testid="fournisseur-iban" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="form-label">Telephone</Label><Input value={form.telephone} onChange={e => set("telephone", e.target.value)} data-testid="fournisseur-tel" /></div>
              <div><Label className="form-label">Email</Label><Input value={form.email} onChange={e => set("email", e.target.value)} data-testid="fournisseur-email" /></div>
            </div>
            <div><Label className="form-label">Adresse</Label><Input value={form.adresse} onChange={e => set("adresse", e.target.value)} data-testid="fournisseur-adresse" /></div>
            <div className="grid grid-cols-3 gap-3">
              <div><Label className="form-label">Code postal</Label><Input value={form.code_postal} onChange={e => set("code_postal", e.target.value)} /></div>
              <div><Label className="form-label">Ville</Label><Input value={form.ville} onChange={e => set("ville", e.target.value)} /></div>
              <div><Label className="form-label">Pays</Label><Input value={form.pays} onChange={e => set("pays", e.target.value)} /></div>
            </div>
            <div><Label className="form-label">Notes</Label><Textarea value={form.notes} onChange={e => set("notes", e.target.value)} rows={2} data-testid="fournisseur-notes" /></div>
            <div className="flex justify-end gap-3 pt-2 border-t">
              <Button variant="outline" onClick={() => setDialogOpen(false)}>Annuler</Button>
              <Button onClick={handleSave} disabled={saving} className="bg-orange-600 hover:bg-orange-700 text-white" data-testid="fournisseur-save-btn">
                {saving ? "..." : editId ? "Mettre a jour" : "Creer"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Fournisseurs;
